import { Stack, Text, Timeline } from "@mantine/core"
import { ChecksIcon, FileTextIcon, HandshakeIcon, SignatureIcon, TruckIcon } from "@phosphor-icons/react"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import type { DeliveryActionState } from "../delivery/delivery-actions"
import { DnAuditLogPanel } from "./audit-log-panel"
import { DnDetail, formatDateTime } from "./dn-detail-utils"
import { HandoverLogPanel } from "./handover-log-panel"
import classes from "./dn-detail.module.css"

/** «Түүх» tab: lifecycle milestones from the DN dates, then the audit journal with «Тэмдэглэл нэмэх» (O94). */
export function DnTimeline({ dn, staffName, addNoteState }: Readonly<{ dn: DnDetail; staffName: string | null; addNoteState: DeliveryActionState }>) {
  const { t } = useTranslation("delivery")
  const steps = [
    { key: "created", icon: <FileTextIcon size={14} />, title: t("dnDetail.tlDnCreated"), sub: formatDateTime(dn.createdAt) },
    dn.staffAssignedDate && {
      key: "assigned",
      icon: <SignatureIcon size={14} />,
      title: t("dnDetail.tlAssigned"),
      sub: `${formatDateTime(dn.staffAssignedDate)}${staffName ? t("dnDetail.driverPrefixDot", { name: staffName }) : ""}`,
    },
    dn.loadingDate && { key: "loaded", icon: <HandshakeIcon size={14} />, title: t("dnDetail.tlLoaded"), sub: formatDateTime(dn.loadingDate) },
    dn.dispatchedDate && {
      key: "dispatched",
      icon: <TruckIcon size={14} />,
      title: t("dnDetail.tlDispatched"),
      sub: t("dnDetail.tlDispatchedSub", { date: formatDateTime(dn.dispatchedDate) }),
    },
    dn.deliveredDate && { key: "delivered", icon: <ChecksIcon size={14} />, title: t("dnDetail.tlDelivered"), sub: formatDateTime(dn.deliveredDate) },
  ].filter(Boolean) as { key: string; icon: ReactNode; title: string; sub: string }[]

  return (
    <>
      <Timeline active={steps.length - 1} bulletSize={28} lineWidth={2} color="gray">
        {steps.map((s) => (
          <Timeline.Item key={s.key} bullet={s.icon} title={<Text fz="var(--nw-fz-body)" fw={650}>{s.title}</Text>}>
            <Text className={classes.label}>{s.sub}</Text>
          </Timeline.Item>
        ))}
      </Timeline>
      {dn.id && (
        <Stack gap="lg" mt="lg">
          <HandoverLogPanel deliveryNoteId={dn.id} />
          <DnAuditLogPanel deliveryNoteId={dn.id} addNoteState={addNoteState} />
        </Stack>
      )}
    </>
  )
}
